import {
  authOverlayEl,
  authNameInputEl,
  authSigninBtnEl,
  authRegisterBtnEl,
  authCloseBtnEl,
  authStatusEl,
  lobbyLogoutBtnEl,
  lobbyRoomNameInputEl,
  lobbyIdentityPillEl,
  lobbyNavBotlabBtnEl
} from '../ui/dom.js';
import { authState, net, lobby, lobbyRuntime } from '../engine/state.js';
import { setStatus } from '../ui/status.js';
import { authJson } from '../network/api.js';
import { closeProfile } from '../ui/profile.js';
import {
  setLobbyVisible,
  syncStageVisibility,
  renderLobby,
  fetchRoomDirectory,
  joinRoom,
  enterRoomById,
  returnToLobbyBrowser,
  sanitizeRoomId
} from '../ui/lobby.js';

function hasPasskeySupport() {
  return Boolean(window.PublicKeyCredential && navigator.credentials);
}

function bytesToBase64Url(bytesLike) {
  const view = bytesLike instanceof Uint8Array
    ? bytesLike
    : new Uint8Array(bytesLike);
  let binary = '';
  for (let i = 0; i < view.length; i++) {
    binary += String.fromCharCode(view[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value) {
  const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const padLen = normalized.length % 4 === 0 ? 0 : 4 - (normalized.length % 4);
  const base64 = `${normalized}${'='.repeat(padLen)}`;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function decodeRegisterOptions(options) {
  return {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBytes(options.user.id)
    },
    excludeCredentials: Array.isArray(options.excludeCredentials)
      ? options.excludeCredentials.map((cred) => ({
        ...cred,
        id: base64UrlToBytes(cred.id)
      }))
      : []
  };
}

function decodeLoginOptions(options) {
  return {
    ...options,
    challenge: base64UrlToBytes(options.challenge),
    allowCredentials: Array.isArray(options.allowCredentials)
      ? options.allowCredentials.map((cred) => ({
        ...cred,
        id: base64UrlToBytes(cred.id)
      }))
      : []
  };
}

function serializeAttestationCredential(credential) {
  const response = credential.response;
  const publicKey = typeof response.getPublicKey === 'function'
    ? response.getPublicKey()
    : null;
  const authenticatorData = typeof response.getAuthenticatorData === 'function'
    ? response.getAuthenticatorData()
    : null;
  const transports = typeof response.getTransports === 'function'
    ? response.getTransports()
    : [];

  return {
    id: credential.id,
    rawId: bytesToBase64Url(new Uint8Array(credential.rawId)),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64Url(new Uint8Array(response.clientDataJSON)),
      attestationObject: bytesToBase64Url(new Uint8Array(response.attestationObject)),
      publicKey: publicKey ? bytesToBase64Url(new Uint8Array(publicKey)) : null,
      publicKeyAlgorithm: typeof response.getPublicKeyAlgorithm === 'function'
        ? response.getPublicKeyAlgorithm()
        : null,
      authenticatorData: authenticatorData ? bytesToBase64Url(new Uint8Array(authenticatorData)) : null,
      transports
    }
  };
}

function serializeAssertionCredential(credential) {
  const response = credential.response;
  return {
    id: credential.id,
    rawId: bytesToBase64Url(new Uint8Array(credential.rawId)),
    type: credential.type,
    response: {
      authenticatorData: bytesToBase64Url(new Uint8Array(response.authenticatorData)),
      clientDataJSON: bytesToBase64Url(new Uint8Array(response.clientDataJSON)),
      signature: bytesToBase64Url(new Uint8Array(response.signature)),
      userHandle: response.userHandle ? bytesToBase64Url(new Uint8Array(response.userHandle)) : null
    }
  };
}

function setAuthStatus(text, isError = false) {
  if (!authStatusEl) return;
  authStatusEl.textContent = text;
  authStatusEl.classList.toggle('error', Boolean(isError));
}

function setAuthBusy(busy) {
  authState.busy = Boolean(busy);
  const passkeyBlocked = authState.busy || !hasPasskeySupport();
  if (authSigninBtnEl) authSigninBtnEl.disabled = passkeyBlocked;
  if (authRegisterBtnEl) authRegisterBtnEl.disabled = passkeyBlocked;
  if (authNameInputEl) authNameInputEl.disabled = authState.busy;
}

// The sign-in modal. It opens over the lobby and is only ever asked for: no
// visitor is stopped by it, because everyone starts as an assigned guest.
function showAuthOverlay(show) {
  if (!authOverlayEl) return;
  authOverlayEl.classList.toggle('hidden', !show);
  authOverlayEl.setAttribute('aria-hidden', show ? 'false' : 'true');
  syncStageVisibility();
  if (show && authNameInputEl) {
    // After the open transition starts, so the focus ring lands on the field
    // rather than on the button that opened the modal.
    setTimeout(() => authNameInputEl.focus(), 0);
  }
}

function openAuthModal() {
  if (!hasPasskeySupport()) {
    setAuthStatus('Passkeys are not supported in this browser, so accounts are not available here.', true);
  } else if (!authState.busy) {
    setAuthStatus('');
  }
  setAuthBusy(authState.busy);
  showAuthOverlay(true);
}

function closeAuthModal() {
  showAuthOverlay(false);
}

function isValidUsername(value) {
  return (
    typeof value === 'string' &&
    value.length >= 2 &&
    value.length <= 24 &&
    /^[A-Za-z0-9_.\- ]+$/.test(value)
  );
}

function activeUsernameFromInput() {
  const raw = typeof authNameInputEl?.value === 'string' ? authNameInputEl.value.trim() : '';
  if (!raw) return null;
  if (!isValidUsername(raw)) return null;
  return raw;
}

function suggestGuestUsername() {
  const suffix = String(Math.floor(1000 + Math.random() * 9000));
  return `Guest ${suffix}`;
}

// Last-used name survives reloads so returning players (guest or passkey)
// land on a prefilled form. The passkey session username still wins.
const PLAYER_NAME_STORE_KEY = 'curvegen.playerName.v1';

function rememberPlayerName(name) {
  try {
    window.localStorage.setItem(PLAYER_NAME_STORE_KEY, String(name));
  } catch (_) { }
}

function recallPlayerName() {
  try {
    const raw = window.localStorage.getItem(PLAYER_NAME_STORE_KEY);
    if (!raw || !isValidUsername(raw)) return '';
    // Assigned guest handles are never prefilled; the field is for passkey names.
    return /^Guest \d+$/.test(raw) ? '' : raw;
  } catch (_) {
    return '';
  }
}

// Guest identity survives a refresh but not the tab: sessionStorage keeps a
// mid-game reload from bouncing the guest back to the login page, while a
// fresh tab still starts at the modal. Logging in (or out) clears it.
const GUEST_SESSION_KEY = 'curvegen.guestSession.v1';

function storeGuestSession(name) {
  try {
    window.sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify({
      name: String(name),
      savedAt: Date.now()
    }));
  } catch (_) { }
}

function readGuestSession() {
  try {
    const raw = window.sessionStorage.getItem(GUEST_SESSION_KEY);
    if (!raw) return '';
    const value = JSON.parse(raw);
    return typeof value?.name === 'string' && value.name ? value.name : '';
  } catch (_) {
    return '';
  }
}

function clearGuestSession() {
  try {
    window.sessionStorage.removeItem(GUEST_SESSION_KEY);
  } catch (_) { }
}

function devAutoGuestName() {
  const raw = typeof window.DEV_AUTO_GUEST_NAME === 'string' ? window.DEV_AUTO_GUEST_NAME.trim() : '';
  return raw.slice(0, 32);
}

function devAutoRoomId() {
  const raw = typeof window.DEV_AUTO_ROOM_ID === 'string' ? window.DEV_AUTO_ROOM_ID : '';
  return sanitizeRoomId(raw);
}

function shouldSuppressDevAutoJoinLobby(targetRoomId) {
  const devRoomId = devAutoRoomId();
  return Boolean(devRoomId && targetRoomId && targetRoomId === devRoomId);
}

// Guest-mode UI: the lobby button doubles as Log Out (passkey) / Log In
// (guest), and room naming is reserved for signed-in players.
function syncGuestUi() {
  const guest = Boolean(authState.guest);
  if (lobbyLogoutBtnEl) {
    const labelEl = lobbyLogoutBtnEl.querySelector('span');
    if (labelEl) labelEl.textContent = guest ? 'Log In' : 'Log Out';
    const iconEl = lobbyLogoutBtnEl.querySelector('i');
    if (iconEl) {
      iconEl.classList.toggle('bi-box-arrow-left', !guest);
      iconEl.classList.toggle('bi-box-arrow-in-right', guest);
    }
    lobbyLogoutBtnEl.title = guest ? 'Sign in or create an account' : 'Log out';
    lobbyLogoutBtnEl.classList.toggle('is-login', guest);
  }
  if (lobbyIdentityPillEl) {
    // Signed in, the pill opens the profile (profile.js). A guest has no
    // profile, so for them it opens the sign-in modal instead: the guest's own
    // name is where they look to make it theirs.
    lobbyIdentityPillEl.title = guest
      ? 'Sign in or create an account'
      : 'Open your profile';
  }
  if (lobbyNavBotlabBtnEl) {
    // Same split: a bot model is stored on an account, so a guest is sent to
    // sign in first rather than shown a disabled item.
    lobbyNavBotlabBtnEl.title = guest
      ? 'Sign in to upload a bot model'
      : 'Upload and manage your bot model';
  }
  if (lobbyRoomNameInputEl) {
    lobbyRoomNameInputEl.disabled = guest;
    lobbyRoomNameInputEl.placeholder = guest ? 'Sign in to name rooms' : 'Room name';
    if (guest) lobbyRoomNameInputEl.value = '';
  }
}

async function completeAuth(username, { guest = false } = {}) {
  authState.guest = guest;
  if (guest) {
    storeGuestSession(username);
  } else {
    clearGuestSession();
  }
  syncGuestUi();
  net.playerName = username;
  authState.authenticated = true;
  showAuthOverlay(false);
  lobby.mode = 'browser';
  lobbyRuntime.botActionInFlight = false;
  lobby.phase = 'waiting';
  lobby.connectedPlayers = 0;
  lobby.connectedClients = 0;
  lobby.humanPlayers = 0;
  lobby.botPlayers = 0;
  lobby.manualBotCount = 0;
  lobby.players = [];
  const params = new URLSearchParams(window.location.search);
  const sharedRoomId = sanitizeRoomId(params.get('room') || '');
  const targetRoomId = sharedRoomId || devAutoRoomId();
  const suppressLobbyOverlay = shouldSuppressDevAutoJoinLobby(targetRoomId);
  lobby.message = suppressLobbyOverlay ? 'Joining room...' : 'Create or join a room.';
  if (suppressLobbyOverlay) {
    setLobbyVisible(false);
  } else {
    setLobbyVisible(true);
    renderLobby();
    try {
      await fetchRoomDirectory();
    } catch (_) {
      // Keep lobby open even if room fetch fails.
    }
    renderLobby();
  }
  if (targetRoomId) {
    if (suppressLobbyOverlay) {
      // DEV auto-join only: no lobby is drawn at all, so there is nowhere to
      // put a prompt. A locked room would be refused by the server here.
      await joinRoom(targetRoomId, { suppressLobbyOverlay });
      return;
    }
    lobby.message = 'Joining shared room...';
    renderLobby();
    // A shared link to a LOCKED room lands here, in the lobby, and gets the
    // prompt -- it does not open its own overlay over a half-joined room. The
    // link identifies the room; proving you may enter it is a separate step,
    // always, which is why no link ever carries the password.
    enterRoomById(targetRoomId);
    return;
  }
  setStatus(guest ? `Playing as ${username} (guest)` : `Signed in as ${username}`);
}

// A guest signing in from the modal: undo the guest session first, the same
// way logging out does -- leave any room, and drop the lobby with
// `authenticated` off so the lobby-chat socket opened under the guest name
// closes -- then come back in under the account. Only after the passkey has
// verified, so a cancelled sign-in costs the guest nothing.
async function completeAccountAuth(username) {
  if (authState.authenticated) {
    closeProfile();
    if (lobby.mode !== 'browser') {
      await returnToLobbyBrowser();
    }
    authState.authenticated = false;
    authState.guest = false;
    setLobbyVisible(false);
  }
  await completeAuth(username);
}

async function beginGuestPlay() {
  if (authState.busy) return;
  // Guest names are assigned, never typed: chosen names stay reserved for
  // passkey accounts, and the server enforces the same rule.
  const username = suggestGuestUsername();
  setAuthBusy(true);
  setAuthStatus(`Entering the arena as ${username}...`);
  try {
    await completeAuth(username, { guest: true });
  } finally {
    setAuthBusy(false);
  }
}

async function beginPasskeyRegistration() {
  if (!hasPasskeySupport()) {
    setAuthStatus('Passkeys are not supported in this browser.', true);
    return;
  }
  const username = activeUsernameFromInput();
  if (!username) {
    setAuthStatus('Choose a username (2-24 chars, letters/numbers/._- and spaces).', true);
    return;
  }

  setAuthBusy(true);
  setAuthStatus('Preparing passkey registration...');
  try {
    const optionsPayload = await authJson('/auth/passkey/register/options', 'POST', { username });
    const publicKey = decodeRegisterOptions(optionsPayload.options);
    const created = await navigator.credentials.create({ publicKey });
    if (!created) {
      throw new Error('passkey registration was cancelled');
    }
    setAuthStatus('Finalizing registration...');
    const verifyPayload = {
      username,
      credential: serializeAttestationCredential(created)
    };
    const verified = await authJson('/auth/passkey/register/verify', 'POST', verifyPayload);
    rememberPlayerName(verified.username || username);
    await completeAccountAuth(verified.username || username);
  } catch (error) {
    setAuthStatus(error?.message || 'Passkey registration failed.', true);
  } finally {
    setAuthBusy(false);
  }
}

async function beginPasskeyLogin() {
  if (!hasPasskeySupport()) {
    setAuthStatus('Passkeys are not supported in this browser.', true);
    return;
  }
  const username = activeUsernameFromInput();
  if (!username) {
    setAuthStatus('Enter your username first.', true);
    return;
  }

  setAuthBusy(true);
  setAuthStatus('Waiting for your passkey...');
  try {
    const optionsPayload = await authJson('/auth/passkey/login/options', 'POST', { username });
    const publicKey = decodeLoginOptions(optionsPayload.options);
    const assertion = await navigator.credentials.get({ publicKey });
    if (!assertion) {
      throw new Error('passkey sign-in was cancelled');
    }
    setAuthStatus('Verifying signature...');
    const verifyPayload = {
      username,
      credential: serializeAssertionCredential(assertion)
    };
    const verified = await authJson('/auth/passkey/login/verify', 'POST', verifyPayload);
    rememberPlayerName(verified.username || username);
    await completeAccountAuth(verified.username || username);
  } catch (error) {
    setAuthStatus(error?.message || 'Passkey sign-in failed.', true);
  } finally {
    setAuthBusy(false);
  }
}

// Signing out lands back in the lobby as a fresh guest -- there is no screen
// to be signed out ON any more.
async function logout() {
  if (authState.busy || authState.guest) return;
  if (lobbyLogoutBtnEl) lobbyLogoutBtnEl.disabled = true;
  closeProfile();
  try {
    if (lobby.mode !== 'browser') {
      await returnToLobbyBrowser();
    }
    try {
      await authJson('/auth/logout', 'POST');
    } catch (_) {
      // Sign out locally even if the server call fails.
    }
    authState.authenticated = false;
    authState.guest = false;
    clearGuestSession();
    syncGuestUi();
    setLobbyVisible(false);
    await beginGuestPlay();
    setStatus('Signed out. Playing as a guest.');
  } finally {
    if (lobbyLogoutBtnEl) lobbyLogoutBtnEl.disabled = false;
  }
}

function setupAuthUi() {
  authCloseBtnEl?.addEventListener('click', () => closeAuthModal());
  authOverlayEl?.addEventListener('click', (event) => {
    // The backdrop cancels, same as every other modal here; the card does not.
    if (event.target === authOverlayEl) closeAuthModal();
  });
  authOverlayEl?.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeAuthModal();
  });
  // The ways in, for a guest. Signed in, the same two controls belong to
  // profile.js (it ignores guests), so these ignore everyone else.
  for (const btn of [lobbyIdentityPillEl, lobbyNavBotlabBtnEl]) {
    btn?.addEventListener('click', () => {
      if (authState.guest) openAuthModal();
    });
  }
  if (authSigninBtnEl) {
    authSigninBtnEl.addEventListener('click', () => {
      beginPasskeyLogin();
    });
  }
  if (authRegisterBtnEl) {
    authRegisterBtnEl.addEventListener('click', () => {
      beginPasskeyRegistration();
    });
  }
  if (authNameInputEl) {
    // Enter follows the primary action: passkey sign-in.
    authNameInputEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      beginPasskeyLogin();
    });
  }
  if (lobbyLogoutBtnEl) {
    // Log In for a guest, Log Out for an account (see syncGuestUi).
    lobbyLogoutBtnEl.addEventListener('click', () => {
      if (authState.guest) {
        openAuthModal();
        return;
      }
      void logout();
    });
  }
}

async function bootstrapAuthAndStart() {
  const params = new URLSearchParams(window.location.search);
  const queryName = params.get('name');
  const devGuestName = devAutoGuestName();
  if (authNameInputEl) {
    authNameInputEl.value = recallPlayerName();
  }

  if (devGuestName) {
    if (authNameInputEl) {
      authNameInputEl.value = devGuestName;
    }
    await completeAuth(devGuestName, { guest: true });
    return;
  }

  if (!authOverlayEl) {
    await completeAuth(queryName || net.playerName, { guest: true });
    return;
  }

  if (!hasPasskeySupport()) {
    // Guests can still play; only the passkey buttons go dark.
    setAuthBusy(false);
    const guestName = readGuestSession();
    if (guestName) {
      await completeAuth(guestName, { guest: true });
      return;
    }
    await beginGuestPlay();
    return;
  }

  try {
    const session = await authJson('/auth/session', 'GET');
    if (session?.authenticated && typeof session.username === 'string' && session.username) {
      if (authNameInputEl) authNameInputEl.value = session.username;
      await completeAuth(session.username);
      return;
    }
  } catch (_) {
    // No account session: fall through to the guest paths below.
  }

  // No passkey session, but a live guest session in this tab: resume it so a
  // refresh mid-room keeps the same guest name.
  const guestName = readGuestSession();
  if (guestName) {
    await completeAuth(guestName, { guest: true });
    return;
  }
  // Nobody to resume: straight into the lobby as a new guest. Signing in is
  // the modal's job, opened from the rail whenever they want it.
  await beginGuestPlay();
}

export { setupAuthUi, bootstrapAuthAndStart };
